import { useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import * as audioOps from '../../domain/audioOps';
import { AUDIO_LANES, MAX_SONG_BARS, STEPS_PER_BAR, type AudioClip, type SectionKind } from '../../domain/schemas';
import { createId } from '../../lib/ids';
import { useObservable } from '../../lib/useObservable';
import { useProject } from '../../state/projectContext';
import { useServices } from '../../state/services';
import { useOpRunner } from '../music/useOpRunner';
import { Alert, Button, Panel } from '../ui';

const PX_PER_STEP = 6;
const LANE_HEIGHT = 44;
const SECTION_TINT: Record<SectionKind, string> = {
  intro: 'bg-sky-900/60',
  verse: 'bg-emerald-900/60',
  buildup: 'bg-amber-900/60',
  drop: 'bg-fuchsia-900/60',
  breakdown: 'bg-indigo-900/60',
  outro: 'bg-zinc-700/60',
};
const selectClass = 'rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm';

const barBeat = (step: number) => {
  const bar = Math.floor(step / STEPS_PER_BAR) + 1;
  const beat = Math.floor((step % STEPS_PER_BAR) / 4) + 1;
  const sixteenth = Math.round((step % 4) * 100) / 100 + 1;
  return `${bar}.${beat}.${sixteenth}`;
};

interface Drag {
  clipId: string;
  pointerId: number;
  x: number;
  y: number;
  startStep: number;
  lane: number;
  moved: boolean;
}

/**
 * Song timeline for audio clips: one row per lane, positions on the arrangement's
 * 16th-note grid. Clips can be dragged (with snapping) or moved with the keyboard;
 * a move that would overlap another clip is refused with a message.
 */
export function ClipTimeline() {
  const { project } = useProject();
  const { audio, selection } = useServices();
  const engine = useObservable(audio.state);
  const selected = useObservable(selection);
  const { apply, error, clearError } = useOpRunner();
  const [snap, setSnap] = useState<audioOps.SnapGrid>('beat');
  const [drag, setDrag] = useState<Drag | null>(null);
  const [preview, setPreview] = useState<{ startStep: number; lane: number } | null>(null);
  const [placeBar, setPlaceBar] = useState(1);
  const scroller = useRef<HTMLDivElement>(null);
  const bpm = project.settings.bpm;
  const songSteps = audioOps.songLengthSteps(project);
  const clipsEnd = Math.max(0, ...project.audioClips.map((c) => audioOps.clipEndStep(c, bpm)));
  const totalSteps = Math.min(MAX_SONG_BARS * STEPS_PER_BAR, Math.max(songSteps, Math.ceil(clipsEnd / STEPS_PER_BAR) * STEPS_PER_BAR, 4 * STEPS_PER_BAR) + STEPS_PER_BAR);
  const width = totalSteps * PX_PER_STEP;
  const barEvery = totalSteps / STEPS_PER_BAR > 64 ? 8 : totalSteps / STEPS_PER_BAR > 24 ? 4 : 1;
  const selectedSample = project.assets.find((a) => a.id === selected.sampleId && a.kind === 'audio');
  const playing = engine.status === 'playing' && project.transport.mode === 'song';
  const gridSteps = audioOps.SNAP_GRIDS[snap] || 1;

  const move = (clip: AudioClip, to: { startStep?: number; lane?: number }) => apply((p) => audioOps.moveClip(p, clip.id, to));

  const onPointerDown = (event: PointerEvent<HTMLButtonElement>, clip: AudioClip) => {
    if (event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    selection.selectClip(clip.id);
    clearError();
    setDrag({ clipId: clip.id, pointerId: event.pointerId, x: event.clientX, y: event.clientY, startStep: clip.startStep, lane: clip.lane, moved: false });
  };
  const onPointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    if (!drag || event.pointerId !== drag.pointerId) return;
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (!drag.moved && Math.abs(dx) < 3 && Math.abs(dy) < 3) return;
    if (!drag.moved) setDrag({ ...drag, moved: true });
    setPreview({
      startStep: audioOps.snapStep(drag.startStep + dx / PX_PER_STEP, snap),
      lane: Math.max(0, Math.min(AUDIO_LANES - 1, drag.lane + Math.round(dy / LANE_HEIGHT))),
    });
  };
  const onPointerUp = (event: PointerEvent<HTMLButtonElement>, clip: AudioClip) => {
    if (!drag || event.pointerId !== drag.pointerId) return;
    if (drag.moved && preview) move(clip, preview);
    setDrag(null);
    setPreview(null);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, clip: AudioClip) => {
    const step = event.shiftKey ? STEPS_PER_BAR : gridSteps;
    switch (event.key) {
      case 'ArrowLeft':
        move(clip, { startStep: audioOps.snapStep(Math.max(0, clip.startStep - step), snap === 'off' ? 'off' : snap) });
        break;
      case 'ArrowRight':
        move(clip, { startStep: audioOps.snapStep(clip.startStep + step, snap === 'off' ? 'off' : snap) });
        break;
      case 'ArrowUp':
        if (clip.lane > 0) move(clip, { lane: clip.lane - 1 });
        break;
      case 'ArrowDown':
        if (clip.lane < AUDIO_LANES - 1) move(clip, { lane: clip.lane + 1 });
        break;
      case 'Delete':
      case 'Backspace':
        if (apply((p) => audioOps.removeClip(p, clip.id))) selection.selectClip(null);
        break;
      case 'd':
      case 'D': {
        const id = createId('clp');
        if (apply((p) => audioOps.duplicateClip(p, clip.id, id))) selection.selectClip(id);
        break;
      }
      default:
        return;
    }
    event.preventDefault();
  };

  const placeSelected = () => {
    if (!selectedSample) return;
    const id = createId('clp');
    if (apply((p) => audioOps.addClip(p, selectedSample.id, { startStep: (placeBar - 1) * STEPS_PER_BAR, id }))) selection.selectClip(id);
  };

  const sectionStarts = project.arrangement.sections.reduce<number[]>((acc, _s, i) => [...acc, i === 0 ? 0 : acc[i - 1]! + project.arrangement.sections[i - 1]!.bars * STEPS_PER_BAR], []);
  return (
    <Panel
      title="Audio clip timeline"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="clip-snap" className="text-xs text-zinc-300">
            Snap
          </label>
          <select id="clip-snap" value={snap} onChange={(e) => setSnap(e.target.value as audioOps.SnapGrid)} className={selectClass}>
            <option value="bar">Bar</option>
            <option value="beat">Beat</option>
            <option value="step">16th</option>
            <option value="off">Off</option>
          </select>
        </div>
      }
    >
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1">
          <label htmlFor="place-bar" className="text-xs text-zinc-300">
            Place selected sample at bar
          </label>
          <input
            id="place-bar"
            type="number"
            min={1}
            max={MAX_SONG_BARS}
            value={placeBar}
            onChange={(e) => setPlaceBar(Math.max(1, Math.min(MAX_SONG_BARS, Math.round(Number(e.target.value) || 1))))}
            className="w-20 rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm"
          />
        </div>
        <Button variant="primary" onClick={placeSelected} disabled={!selectedSample || !selectedSample.audio} data-testid="place-sample">
          Add to timeline
        </Button>
        <span className="text-xs text-zinc-400">
          {selectedSample ? (selectedSample.audio ? `Selected: ${selectedSample.name}` : 'Preview the selected sample once so it can be analysed.') : 'Select a sample in the library to place it.'}
        </span>
      </div>
      {error && (
        <div className="mb-3" aria-live="assertive">
          <Alert title={error} action={<Button size="sm" variant="ghost" onClick={clearError}>Dismiss</Button>} />
        </div>
      )}
      <p id="clip-keys" className="mb-2 text-xs text-zinc-400">
        Drag clips to move them between bars and lanes. With a clip focused: ←/→ move by the snap grid (Shift: one bar), ↑/↓ change lane, D duplicates,
        Delete removes.
      </p>
      <div ref={scroller} className="overflow-x-auto rounded-md border border-zinc-800 bg-zinc-950" data-testid="clip-timeline">
        <div className="relative" style={{ width }}>
          {/* Ruler with sections and bar numbers */}
          <div className="relative h-10 border-b border-zinc-800" aria-hidden="true">
            {project.arrangement.sections.map((s, i) => {
              const left = sectionStarts[i]! * PX_PER_STEP;
              return (
                <div key={s.id} className={`absolute top-0 h-4 truncate border-r border-zinc-900 px-1 text-[10px] text-zinc-200 ${SECTION_TINT[s.kind]}`} style={{ left, width: s.bars * STEPS_PER_BAR * PX_PER_STEP }}>
                  {s.name}
                </div>
              );
            })}
            {Array.from({ length: Math.ceil(totalSteps / STEPS_PER_BAR) }, (_, bar) =>
              bar % barEvery === 0 ? (
                <span key={bar} className="absolute top-5 border-l border-zinc-700 pl-0.5 text-[10px] text-zinc-400" style={{ left: bar * STEPS_PER_BAR * PX_PER_STEP }}>
                  {bar + 1}
                </span>
              ) : null,
            )}
            <div className="absolute top-0 bottom-0 border-l-2 border-dashed border-red-500/60" style={{ left: songSteps * PX_PER_STEP }} title="End of song" />
          </div>
          {/* Lanes */}
          <div role="group" aria-label="Clip lanes" aria-describedby="clip-keys" className="relative" style={{ height: LANE_HEIGHT * AUDIO_LANES }}>
            {Array.from({ length: AUDIO_LANES }, (_, lane) => (
              <div key={lane} className="absolute right-0 left-0 border-b border-zinc-900" style={{ top: lane * LANE_HEIGHT, height: LANE_HEIGHT }}>
                <span className="sticky left-0 pl-1 text-[10px] text-zinc-400">Lane {lane + 1}</span>
              </div>
            ))}
            {Array.from({ length: Math.ceil(totalSteps / STEPS_PER_BAR) }, (_, bar) => (
              <div key={bar} aria-hidden="true" className="absolute top-0 bottom-0 border-l border-zinc-800/60" style={{ left: bar * STEPS_PER_BAR * PX_PER_STEP }} />
            ))}
            {project.audioClips.map((clip) => {
              const isDragging = drag?.clipId === clip.id && drag.moved && preview;
              const startStep = isDragging ? preview.startStep : clip.startStep;
              const lane = isDragging ? preview.lane : clip.lane;
              const lengthSteps = audioOps.clipLengthSteps(clip, bpm);
              const isSelected = selected.clipId === clip.id;
              const pastEnd = startStep + lengthSteps > songSteps;
              const asset = project.assets.find((a) => a.id === clip.assetId);
              return (
                <button
                  key={clip.id}
                  type="button"
                  aria-pressed={isSelected}
                  aria-label={`${clip.name}, lane ${clip.lane + 1}, starts at bar ${barBeat(clip.startStep)}, ${(audioOps.clipTimelineSeconds(clip)).toFixed(2)} seconds${clip.muted ? ', muted' : ''}${clip.reverse ? ', reversed' : ''}`}
                  data-testid="clip"
                  data-clip-id={clip.id}
                  data-start-step={clip.startStep}
                  data-lane={clip.lane}
                  onPointerDown={(e) => onPointerDown(e, clip)}
                  onPointerMove={onPointerMove}
                  onPointerUp={(e) => onPointerUp(e, clip)}
                  onPointerCancel={() => {
                    setDrag(null);
                    setPreview(null);
                  }}
                  onKeyDown={(e) => onKeyDown(e, clip)}
                  onFocus={() => selection.selectClip(clip.id)}
                  className={`absolute touch-none overflow-hidden rounded border px-1 text-left text-[11px] leading-tight select-none ${
                    clip.muted ? 'border-zinc-600 bg-zinc-700/50 text-zinc-400' : 'border-teal-500 bg-teal-800/70 text-teal-50'
                  } ${isSelected ? 'z-10 ring-2 ring-white' : ''} ${isDragging ? 'opacity-80' : ''}`}
                  style={{ left: startStep * PX_PER_STEP, top: lane * LANE_HEIGHT + 3, width: Math.max(10, lengthSteps * PX_PER_STEP), height: LANE_HEIGHT - 6 }}
                >
                  <span className="block truncate font-medium">
                    {clip.reverse ? '◀ ' : ''}
                    {clip.name}
                  </span>
                  <span className="block truncate text-[10px] opacity-80">
                    {clip.loops > 1 ? `×${clip.loops} · ` : ''}
                    {clip.gainDb !== 0 ? `${clip.gainDb > 0 ? '+' : ''}${clip.gainDb} dB · ` : ''}
                    {clip.playbackRate !== 1 ? `${clip.playbackRate}× · ` : ''}
                    {!asset ? 'missing file' : pastEnd ? 'past song end' : barBeat(clip.startStep)}
                  </span>
                  {clip.loops > 1 &&
                    Array.from({ length: clip.loops - 1 }, (_, i) => (
                      <span key={i} aria-hidden="true" className="absolute top-0 bottom-0 border-l border-dashed border-teal-200/50" style={{ left: ((i + 1) * lengthSteps * PX_PER_STEP) / clip.loops }} />
                    ))}
                </button>
              );
            })}
            {playing && engine.step >= 0 && (
              <div aria-hidden="true" data-testid="clip-playhead" className="pointer-events-none absolute top-0 bottom-0 z-20 w-0.5 bg-amber-300" style={{ left: engine.step * PX_PER_STEP }} />
            )}
          </div>
        </div>
      </div>
      <p className="mt-2 text-xs text-zinc-400">
        {project.audioClips.length} clip{project.audioClips.length === 1 ? '' : 's'} · clips play in Song mode, through the audio bus. Positions follow the tempo;
        the audio itself is never stretched.
      </p>
    </Panel>
  );
}
